import React, {useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {useComponentProps} from 'helpers/hooks/utils';
import utils from 'helpers/utils';
import StyledCollectionTagGroupProfileCard
  from 'components/organisms/Cards/CollectionTagGroupProfileCard/CollectionTagGroupProfileCard.styles';
import {useDialogControl} from 'components/organisms/Providers/DialogProvider/DialogProvider';
import ConfirmDialog from 'components/organisms/Dialogs/ConfirmDialog/ConfirmDialog';
import ActionIconButton from 'components/molecules/Buttons/ActionIconButton/ActionIconButton';
import Icon from 'components/atoms/Icons/Icon/Icon';
import Delete from '@mui/icons-material/Delete';
import CardHeader from 'components/atoms/Cards/CardHeader/CardHeader';
import {DragIndicator} from '@mui/icons-material';
import {DragHandleContext, useDragHandle} from 'components/organisms/Utils/DragDrop/DndContext';

const CollectionTagGroupProfileCard = React.forwardRef((props, ref) => {
  const {
    card,
    action,
    onHide,
    tagGroup,
    collection,
    fieldData,
    isDialog,
    renderedContent,
    ...innerProps
  } = useComponentProps(props, 'CollectionTagGroupProfileCard', {
    static: ['isAddNew', 'isReadOnly', 'isEditing', 'isDeleted', 'isDialog'],
    children: ['content']
  });

  const innerRef = useRef(null);

  const [isEditing, setIsEditing] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);

  useImperativeHandle(ref, () => innerRef.current);

  const isAddNew = !utils.isDefined(tagGroup?.groupId);
  const isReadOnly = !card.card.editable;
  const isNewCollection = !(collection.collectionId > 0);
  const isLoading = !utils.isDefined(tagGroup?.name) || !utils.isDefined(tagGroup?.tags);

  const dialogControl = useDialogControl();

  useLayoutEffect(() => {
    const editing = card.state.isEditing ?? card.state.card.isEditing ?? card.state.dialog.isEditing;
    if (!isAddNew) {
      setIsEditing(editing);
    } else {
      if (!card.state.card.submitSuccess) { // wait for new card
        setIsEditing(editing);
      }
    }
  }, [card.state.isEditing, card.state.card.isEditing, card.state.dialog.isEditing, card.state.card.submitSuccess, isAddNew]);

  const handleDeleteClick = (e) => {
    const handleConfirm = () => {
      const field = {
        name: `tagGroup-${tagGroup.groupId}`
      };

      return utils.asPromiseCallback(card.card.onPatch)(field, null)
        .then(() => {
          onHide?.(true);
          setIsDeleted(true);
        });
    }

    if (!isNewCollection) {
      dialogControl.show(<ConfirmDialog question="Are you sure you want to remove this category?"
                                        explanation="The category will be removed from this collection"
                                        challenge={tagGroup.name}
                                        challengeLabel="category name"
                                        onConfirm={handleConfirm}
                                        ConfirmButtonProps={{
                                          children: 'Remove category',
                                          startIcon: <Icon icon={Delete} />,
                                          color: 'error'
                                        }}/>, true);
    } else {
      handleConfirm();
    }

    e.preventDefault();
  };

  const dragHandleContext = useDragHandle(DragHandleContext);

  const dragAction = useMemo(() => ({
    icon: <Icon icon={DragIndicator} size="smaller" />,
    tooltip: 'Move category',
    TooltipProps: dragHandleContext?.isDragging ? {
      open: false
    } : null,
    onClick: () => null,
    ActionIconButtonProps: {
      IconProps: {size: 'tiny'}
    },
    IconButtonProps: {
      ...utils.filterObject(dragHandleContext, 'isDragging'),
      className: 'ProfileCard-action dragHandle'
    }
  }), [dragHandleContext]);

  const deleteAction = useMemo(() => ({
    icon: <Icon icon={Delete} size="smaller" />,
    tooltip: 'Remove category',
    ActionIconButtonProps: {
      IconProps: {size: 'tiny'}
    },
    IconButtonProps: {
      onPointerDown: (e) => {
        e.preventDefault();
      }
    }
  }), []);

  const renderActions = () => {
    if (action && !isLoading) {
      return <React.Fragment>
        {dragHandleContext ? <ActionIconButton tabIndex={-1}
                                               action={dragAction}
                                               density="dense"
                                               disableRipple={true}
                                               size="smaller" variant="outlined" /> : null}
        {!(isEditing || isAddNew || isReadOnly) ? <ActionIconButton className="ProfileCard-action"
                                                                    variant="contained"
                                                                    size="smaller"
                                                                    color="error"
                                                                    density="dense"
                                                                    tabIndex={-1}
                                                                    action={deleteAction}
                                                                    onClick={handleDeleteClick} /> : null}
        <ActionIconButton className="ProfileCard-action"
                          tabIndex={-1}
                          action={action} density="dense"
                          size="smaller" variant="outlined"
                          IconProps={{size: 'tiny'}}
                          onPointerDown={(e) => {
                            e.preventDefault();
                          }}/>
      </React.Fragment>
    }
  }

  innerProps.className = utils.flattenClassName(innerProps.className, {
    isAddNew: isAddNew,
    isReadOnly: isReadOnly,
    isEditing: isEditing,
    isDeleted: isDeleted
  });

  return <StyledCollectionTagGroupProfileCard ref={innerRef} {...innerProps}>
    {!(isAddNew || isDialog) ? <CardHeader className="ProfileCard-header"
                                           action={renderActions()}/> : null}
    {renderedContent}
  </StyledCollectionTagGroupProfileCard>
});

CollectionTagGroupProfileCard.propTypes = {
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.func
  ]),
  card: PropTypes.object,
  action: PropTypes.object,
  onHide: PropTypes.func,
  tagGroup: PropTypes.object,
  collection: PropTypes.object,
  fieldData: PropTypes.object,
  isDialog: PropTypes.bool,
  renderedContent: PropTypes.any
};

export default CollectionTagGroupProfileCard;
